export type HeatmapDownloadAssetReason =
  | "invalid_response"
  | "decode_failed"
  | "fetch_failed";

const DOWNLOAD_ASSET_REASONS = new Set<HeatmapDownloadAssetReason>([
  "invalid_response",
  "decode_failed",
  "fetch_failed",
]);

const DOWNLOAD_ASSET_MESSAGES: Record<HeatmapDownloadAssetReason, string> = {
  decode_failed: "Screenshot image could not be decoded",
  invalid_response: "Screenshot proxy returned invalid image data",
  fetch_failed: "Screenshot could not be fetched for export",
};

export class HeatmapDownloadAssetError extends Error {
  reason: HeatmapDownloadAssetReason;

  constructor(reason: HeatmapDownloadAssetReason) {
    super(DOWNLOAD_ASSET_MESSAGES[reason]);
    this.name = "HeatmapDownloadAssetError";
    this.reason = reason;
  }
}

export const isHeatmapDownloadAssetError = (
  error: unknown,
): error is HeatmapDownloadAssetError =>
  error instanceof HeatmapDownloadAssetError ||
  (typeof error === "object" &&
    error !== null &&
    (error as { name?: unknown }).name === "HeatmapDownloadAssetError" &&
    DOWNLOAD_ASSET_REASONS.has((error as { reason?: unknown }).reason as HeatmapDownloadAssetReason));

/**
 * Download failures happen after the on-screen <img> already loaded, so
 * recapture cannot fix CORS, an unimplemented screenshot proxy, or a
 * transient fetch. Do not send users into a plan-limited capture job.
 */
export const heatmapDownloadAssetToastMessage = (
  reason: HeatmapDownloadAssetReason,
) => {
  switch (reason) {
    case "decode_failed":
    case "fetch_failed":
    case "invalid_response":
      return "Couldn't export this heatmap image. Try again in a moment.";
  }
};

const SCREENSHOT_FETCH_FAILURE = /failed to fetch|networkerror|load failed/i;

const isScreenshotFetchFailure = (error: unknown) => {
  if (isHeatmapDownloadAssetError(error) || !(error instanceof Error)) return false;
  if (error.name === "AbortError" || error.name === "NetworkError") return true;

  return error instanceof TypeError && SCREENSHOT_FETCH_FAILURE.test(error.message);
};

export const isImageContentType = (contentType: string | null | undefined) =>
  Boolean(contentType?.toLowerCase().split(";")[0].trim().startsWith("image/"));

export const hasKnownImageSignature = (bytes: Uint8Array) => {
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return true;
  }

  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return true;
  }

  if (
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x38
  ) {
    return true;
  }

  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return true;
  }

  if (
    bytes[4] === 0x66 &&
    bytes[5] === 0x74 &&
    bytes[6] === 0x79 &&
    bytes[7] === 0x70
  ) {
    return true;
  }

  return false;
};

const HEATMAP_SCREENSHOT_PROXY_PATH = "/api/heatmap-screenshot";

export const heatmapScreenshotProxyUrl = (screenshotUrl: string) =>
  `${HEATMAP_SCREENSHOT_PROXY_PATH}?url=${encodeURIComponent(screenshotUrl)}`;

const isHtmlContentType = (contentType: string | null | undefined) =>
  Boolean(contentType?.toLowerCase().split(";")[0].trim().startsWith("text/html"));

export const assertValidHeatmapScreenshotBlob = async (
  blob: Blob,
  contentType: string | null,
) => {
  if (blob.size === 0) {
    throw new HeatmapDownloadAssetError("invalid_response");
  }

  // Express/SPA fallbacks return index.html with HTTP 200 for the missing
  // screenshot proxy. Never treat that as a heatmap image.
  if (isHtmlContentType(contentType) || isHtmlContentType(blob.type)) {
    throw new HeatmapDownloadAssetError("invalid_response");
  }

  if (isImageContentType(contentType) || isImageContentType(blob.type)) {
    return;
  }

  const signature = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
  if (!hasKnownImageSignature(signature)) {
    throw new HeatmapDownloadAssetError("invalid_response");
  }
};

export const fetchHeatmapScreenshotBlob = async (
  screenshotUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Blob> => {
  const candidates = [screenshotUrl, heatmapScreenshotProxyUrl(screenshotUrl)];
  let lastError: HeatmapDownloadAssetError | null = null;
  let originFetchFailed = false;

  for (const [index, candidate] of candidates.entries()) {
    try {
      const response = await fetchImpl(candidate, { cache: "no-store" });
      if (!response.ok) {
        lastError = new HeatmapDownloadAssetError("invalid_response");
        continue;
      }

      const blob = await response.blob();
      await assertValidHeatmapScreenshotBlob(blob, response.headers.get("content-type"));
      return blob;
    } catch (error) {
      if (isHeatmapDownloadAssetError(error)) {
        lastError = error;
        continue;
      }

      if (isScreenshotFetchFailure(error)) {
        lastError = new HeatmapDownloadAssetError("fetch_failed");
        if (index === 0) originFetchFailed = true;
        continue;
      }

      lastError = new HeatmapDownloadAssetError("invalid_response");
    }
  }

  // Origin CORS/offline plus an unimplemented HTML proxy is still an export
  // pipeline failure — recapture cannot change either condition.
  if (originFetchFailed) {
    throw new HeatmapDownloadAssetError("fetch_failed");
  }

  throw lastError ?? new HeatmapDownloadAssetError("invalid_response");
};
